import { Business } from "@models/Business";
import { User } from "@models/User";
import { setAuthCredentials, setAuthToken } from "@services/apiClient";
import { refreshToken, signIn, signOut } from "@services/authService";
import { fetchBusiness } from "@services/businessService";
import { refreshUserData } from "@services/userService";
import { checkIsBusinessUser } from "@util/user.util";
import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from "react";

interface AuthState {
    isAuthenticated: boolean;
    user: User | null;
    business: Business | null;
}

interface AuthContextProps {
    authState: AuthState;
    isSubscribed: boolean;
    login: (email: string, password: string, captchaToken: string) => Promise<User>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
}

interface AuthProviderProps {
    children: ReactNode;
}

type AuthAction =
    | { type: "LOGIN"; payload: User }
    | { type: "LOGOUT" }
    | { type: "REFRESH_USER"; payload: User }
    | { type: "SET_BUSINESS"; payload: Business | null };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
    switch (action.type) {
        case "LOGIN":
            return {
                ...state,
                isAuthenticated: true,
                user: action.payload,
            };

        case "LOGOUT":
            return {
                isAuthenticated: false,
                user: null,
                business: null,
            };

        case "REFRESH_USER":
            return {
                ...state,
                user: {
                    ...state.user,
                    ...action.payload,
                },
            };

        case "SET_BUSINESS":
            return {
                ...state,
                business: action.payload,
            };

        default:
            return state;
    }
};

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

const REFRESH_INTERVAL = 20 * 60 * 1000; // Minutes

const loadUserFromLocalStorage = (): User | null => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) return null;

    try {
        return JSON.parse(storedUser);
    } catch {
        return null;
    }
};

const saveUserToLocalStorage = (user: User) => {
    localStorage.setItem("user", JSON.stringify(user));
};

const clearStoredUser = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("nextRefresh");
};

const getNextRefreshTime = (): number => {
    const stored = localStorage.getItem("nextRefresh");
    return stored ? parseInt(stored, 10) : Date.now();
};

const setNextRefreshTime = (time: number) => {
    localStorage.setItem("nextRefresh", time.toString());
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const storedUser = loadUserFromLocalStorage();

    if (storedUser) {
        setAuthCredentials(storedUser.token, storedUser.id);
    }

    const [authState, dispatch] = useReducer(authReducer, {
        isAuthenticated: !!storedUser,
        user: storedUser,
        business: null,
    });

    const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

    const stopTokenRefreshTimer = useCallback(() => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }

        localStorage.removeItem("nextRefresh");
    }, []);

    const startTokenRefreshTimer = useCallback(() => {
        if (!authState.isAuthenticated || refreshTimerRef.current) return;

        const now = Date.now();
        const nextRefresh = getNextRefreshTime();
        const delay = nextRefresh > now
            ? nextRefresh - now
            : REFRESH_INTERVAL;

        refreshTimerRef.current = setTimeout(async () => {
            try {
                const token = await refreshToken();

                setAuthToken(token);

                const currentUser = loadUserFromLocalStorage();
                if (currentUser) {
                    saveUserToLocalStorage({ ...currentUser, token });
                }

                setNextRefreshTime(Date.now() + REFRESH_INTERVAL);

                refreshTimerRef.current = null; // clear BEFORE restarting
                startTokenRefreshTimer();
            } catch (err: any) {
                console.warn("Token refresh failed", err);
                // If backend says refresh token is invalid → logout
                if (err?.response?.status === 401 || err?.response?.status === 403) {
                    stopTokenRefreshTimer();
                    dispatch({ type: "LOGOUT" });
                    clearStoredUser();
                    window.location.href = "/auth/sign-in";
                } else {
                    refreshTimerRef.current = null; // clear BEFORE restarting
                    startTokenRefreshTimer();
                }
            }
        }, delay);

        setNextRefreshTime(Date.now() + REFRESH_INTERVAL);
    }, [authState.isAuthenticated, stopTokenRefreshTimer]);

    const login = useCallback(
        async (email: string, password: string, captchaToken: string): Promise<User> => {
            const user = await signIn(email, password, captchaToken);

            dispatch({ type: "LOGIN", payload: user });

            setAuthCredentials(user.token, user.id);

            saveUserToLocalStorage(user);

            if (checkIsBusinessUser(user)) {
                const business = await fetchBusiness(user.businessId!);
                dispatch({ type: "SET_BUSINESS", payload: business ?? null });
            }

            return user;
        },
        [],
    );

    const logout = useCallback(async () => {
        try {
            await signOut();
        } catch {
            // ignore
        }

        stopTokenRefreshTimer();

        dispatch({ type: "LOGOUT" });

        clearStoredUser();
    }, [stopTokenRefreshTimer]);

    const refreshUser = useCallback(async (): Promise<void> => {
        const user = await refreshUserData();

        dispatch({ type: "REFRESH_USER", payload: user });

        const currentUser = loadUserFromLocalStorage();
        if (currentUser) {
            saveUserToLocalStorage({ ...currentUser, ...user });
        }
    }, []);

    const isSubscribed = !["none", "expired", ""].includes(authState.user?.subscription || "");

    const value = useMemo(
        () => ({
            authState,
            isSubscribed,
            login,
            logout,
            refreshUser,
        }),
        [authState, isSubscribed, login, logout, refreshUser],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }

    return context;
};